import React, {
  useState,
  ReactNode,
  HTMLAttributes,
  createContext,
  useContext,
  isValidElement,
  useEffect,
} from 'react'
import Icon from '../Icon'

interface CollapsibleListContextProps {
  activeIndex: number[]
  setActiveIndex: (index: number[] | ((index: number[]) => number[])) => void
  onlyOpenOne: boolean
  /** When false, all items in this list are non-collapsible (no chevron, always expanded) */
  collapsible: boolean
}

interface InternalCollapsibleListItemProps
  extends HTMLAttributes<HTMLDivElement> {
  header: React.ReactNode
  children: ReactNode
  isOpen?: boolean
  index: number // This prop will be injected internally, not passed by the user
  onToggle?: (isOpen: boolean) => void // Add onToggle callback
  /** When false, hides the chevron and keeps content always expanded (non-collapsible) */
  collapsible?: boolean
}

interface PublicCollapsibleListItemProps
  extends Omit<InternalCollapsibleListItemProps, 'index'> {}

const CollapsibleListContext = createContext<
  CollapsibleListContextProps | undefined
>(undefined)

export const useCollapsibleListContext = () => {
  return useContext(CollapsibleListContext)
}

const CollapsibleList: React.FC<
  {
    onlyOpenOne?: boolean
    /** When false, all items in this list are non-collapsible (no chevron, always expanded) */
    collapsible?: boolean
    children: React.ReactNode
  } & React.ReactHTMLElement<HTMLDivElement>['props']
> = ({ onlyOpenOne = false, collapsible = true, children, ...props }) => {
  const [activeIndex, setActiveIndex] = useState<number[]>([])
  return (
    <CollapsibleListContext.Provider
      value={{ activeIndex, setActiveIndex, onlyOpenOne, collapsible }}
    >
      <div
        {...props}
        className={`border rounded-lg border-themed-base-300 border-solid px-4 divide-y divide-themed-base-300 divide-solid bg-themed-white ${props.className}`}
      >
        {React.Children.map(children, (child, index) => {
          if (isValidElement(child)) {
            return React.cloneElement(child, {
              ...child.props,
              children: child.props.children ?? null,
              key: index,
              index, // Inject the index prop here
            })
          }
          return child
        })}
      </div>
    </CollapsibleListContext.Provider>
  )
}

const CollapsibleListItem: React.FC<PublicCollapsibleListItemProps> = ({
  header,
  children,
  isOpen = false,
  onToggle,
  collapsible = true,
  ...props
}) => {
  const { index } = (props as InternalCollapsibleListItemProps) ?? {}
  const context = useCollapsibleListContext()
  const [isNotCollapsed, setIsNotCollapsed] = useState(isOpen)

  useEffect(() => {
    if (!context) return
    if (isOpen) {
      if (context.onlyOpenOne) {
        context.setActiveIndex([index])
      } else {
        context.setActiveIndex((prev) => [...new Set([...prev, index])])
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (context?.onlyOpenOne && context.activeIndex !== null) {
      setIsNotCollapsed(context.activeIndex.includes(index))
    }
  }, [context?.activeIndex])

  // Add effect to call onToggle when state changes
  useEffect(() => {
    const open = context ? context.activeIndex.includes(index) : isNotCollapsed
    onToggle?.(open)
  }, [context?.activeIndex, isNotCollapsed])

  const toggleCollapse = () => {
    const newIsOpen = !isNotCollapsed
    setIsNotCollapsed(newIsOpen)
    if (!context) return
    if (context.onlyOpenOne) {
      context.setActiveIndex(newIsOpen ? [index] : [])
    } else {
      context.setActiveIndex((prev) =>
        newIsOpen
          ? [...new Set([...prev, index])]
          : prev.filter((i) => i !== index),
      )
    }
    // Call onToggle when state changes
    onToggle?.(newIsOpen)
  }

  const isItemCollapsible = (context?.collapsible ?? true) && collapsible
  const open = isItemCollapsible
    ? context
      ? context.activeIndex.includes(index)
      : isNotCollapsed
    : true
  const hasValidChildren = !React.Children.toArray(children).every(
    (child) => child === null || child === undefined,
  )
  const showChevronAndToggle = isItemCollapsible && hasValidChildren
  return (
    <div {...props} className={`w-full py-4 ${props.className}`}>
      <div
        className={`${
          showChevronAndToggle ? 'cursor-pointer' : ''
        } flex justify-between items-center w-full gap-1`}
        onClick={() => {
          if (!showChevronAndToggle) return
          toggleCollapse()
        }}
      >
        <span className="font-semibold overflow-hidden flex-1 min-w-0 max-w-[calc(100%-40px)]">
          {header}
        </span>
        {showChevronAndToggle && (
          <Icon
            name="ChevronDown"
            className={`w-4 h-4 text-themed-base-400 flex-none transform ${
              open ? 'rotate-180' : 'rotate-0'
            } transition-transform duration-300 ease-in-out ml-1`}
          />
        )}
      </div>

      {/* Collapsible content */}
      {showChevronAndToggle && (
        <div
          className={`overflow-hidden grid duration-300 ease-in-out`}
          style={{
            transitionProperty: 'grid-template-rows',
            gridTemplateRows: open ? '1fr' : '0fr',
          }}
        >
          <div className={`transition-all overflow-hidden`}>
            <div className="mt-2">{children}</div>
          </div>
        </div>
      )}
    </div>
  )
}

const Collapsible = {
  List: CollapsibleList,
  ListItem: CollapsibleListItem,
}

export default Collapsible
